import { Box, Button, TextField } from "@mui/material";
import React, { useState } from "react";
import { Link } from "react-admin";
import { useTranslate } from "react-admin";
import { useForm } from "react-hook-form";

import { requiresCompanySignIn } from "../auth/companySignIn";
import { EMAIL_VALIDATION_PATTERN } from "../lib/validation";
import { space } from "../theme/designTokens";
import { AuthFormHeader } from "./AuthFormHeader";

export type LoginFormData = {
  email: string;
  password: string;
};

type LoginFormProps = {
  defaultEmail?: string;
  loading: boolean;
  onSubmit: (data: LoginFormData) => void;
  onCompanySignIn: (email?: string) => void;
  onForgotPassword: (email?: string) => void;
};

// The email decides the next step, so the password field appears only for
// addresses that do not sign in with the company account.
enum LoginStep {
  Email = "email",
  Password = "password",
}

export const LoginForm: React.FC<LoginFormProps> = ({
  defaultEmail = "",
  loading,
  onSubmit,
  onCompanySignIn,
  onForgotPassword,
}) => {
  const translate = useTranslate();
  const [step, setStep] = useState(LoginStep.Email);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<LoginFormData>({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      email: defaultEmail,
      password: "",
    },
  });

  const submitEmail = ({ email }: LoginFormData) => {
    if (requiresCompanySignIn(email)) {
      onCompanySignIn(email);
      return;
    }
    setStep(LoginStep.Password);
  };

  const changeEmail = () => {
    setValue("password", "");
    setStep(LoginStep.Email);
  };

  return (
    <>
      <AuthFormHeader title={translate("login.welcomeTitle")} description={translate("login.welcomeSubtitle")} />

      <Box component="form" onSubmit={handleSubmit(step === LoginStep.Email ? submitEmail : onSubmit)}>
        <TextField
          fullWidth
          label={translate("login.email")}
          type="email"
          margin="normal"
          autoComplete="email"
          autoFocus={step === LoginStep.Email}
          disabled={loading}
          required
          error={!!errors.email}
          helperText={errors.email?.message}
          InputProps={{ readOnly: step === LoginStep.Password }}
          {...register("email", {
            required: translate("login.emailRequired"),
            pattern: {
              value: EMAIL_VALIDATION_PATTERN,
              message: translate("login.emailInvalid"),
            },
          })}
        />

        {step === LoginStep.Password && (
          <TextField
            fullWidth
            label={translate("login.password")}
            type="password"
            margin="normal"
            autoComplete="current-password"
            autoFocus
            disabled={loading}
            error={!!errors.password}
            helperText={errors.password?.message}
            required
            {...register("password", {
              required: translate("login.passwordRequired"),
            })}
          />
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading || !isValid}
          sx={{ marginTop: `${space.xl}px` }}
        >
          {loading
            ? translate("login.signingIn")
            : translate(step === LoginStep.Email ? "login.continue" : "login.signIn")}
        </Button>

        <Box
          sx={{ marginTop: `${space.xl}px`, display: "flex", justifyContent: "space-between", gap: `${space.sm}px` }}
        >
          <Link
            to="#"
            onClick={e => {
              e.preventDefault();
              onForgotPassword(getValues("email"));
            }}
          >
            {translate("login.forgotPassword")}
          </Link>
          {step === LoginStep.Password && (
            <Link
              to="#"
              onClick={e => {
                e.preventDefault();
                changeEmail();
              }}
            >
              {translate("login.changeEmail")}
            </Link>
          )}
        </Box>
      </Box>
    </>
  );
};
